import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, fmtMoney } from "@/lib/api";
import {
  WarningCircle, FilePdf, ArrowSquareOut, Archive, FileArrowUp, CheckCircle, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtCoverage = (s) => {
  if (!s) return "—";
  if (s.coverage_start || s.coverage_end) return `${s.coverage_start || "?"} → ${s.coverage_end || "?"}`;
  return "—";
};

const Row = ({ label, value, match }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
    <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground shrink-0">{label}</span>
    <span className="flex items-center gap-2 text-sm font-semibold text-right min-w-0">
      <span className="truncate">{value || "—"}</span>
      {match === true && <CheckCircle size={16} weight="fill" className="text-success shrink-0" />}
      {match === false && <X size={16} weight="bold" className="text-muted-foreground shrink-0" />}
    </span>
  </div>
);

export default function DuplicateDialog({ bill, open, onOpenChange, onResolved }) {
  if (!bill) return null;
  const sig = bill.dedup_signature || {};
  const old = bill.duplicate_of || {};
  const oldSig = old.dedup_signature || {};

  const same = (a, b) => a != null && b != null && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  const amtSame = sig.amount_due != null && oldSig.amount_due != null && Math.abs(Number(sig.amount_due) - Number(oldSig.amount_due)) < 0.01;
  const covSame = same(sig.coverage_start, oldSig.coverage_start) && same(sig.coverage_end, oldSig.coverage_end);

  const viewSuspected = async () => {
    try {
      const r = await api.get(`/bills/${old.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,width=900,height=1000");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open suspected duplicate"); }
  };

  const resolve = async (action) => {
    try {
      await api.post(`/bills/${bill.id}/resolve-duplicate`, { action });
      onOpenChange(false);
      const msg = action === "file_duplicate" ? "Filed in Archive › Duplicates"
        : action === "revision" ? "Imported as a revised bill — ready to process"
        : "Marked as not a duplicate — ready to process";
      toast.success(msg);
      onResolved && onResolved(action, bill);
    } catch (e) { toast.error("Could not resolve"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="duplicate-dialog" className="rounded-sm max-w-lg border-2 border-destructive">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
            <WarningCircle size={24} weight="fill" className="text-destructive" /> Possible Duplicate
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-1">
          This statement appears to match a document already in your system. Importing it again could double-count expenses in your ledger.
        </p>

        {/* Matched-on details */}
        <div className="border border-border rounded-sm p-4 bg-secondary/40">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Matched on</p>
          <Row label="Vendor" value={sig.vendor_canonical} match={same(sig.vendor_canonical, oldSig.vendor_canonical)} />
          <Row label="Amount due" value={sig.amount_due != null ? fmtMoney(sig.amount_due) : null} match={amtSame} />
          <Row label="Coverage / date" value={fmtCoverage(sig)} match={covSame} />
          <Row label="Property" value={sig.property_label} match={same(sig.property_label, oldSig.property_label)} />
          <Row label="Account #" value={sig.account_number} match={same(sig.account_number, oldSig.account_number)} />
        </div>

        {/* Suspected duplicate link */}
        <button
          data-testid="view-suspected-duplicate"
          onClick={viewSuspected}
          className="w-full text-left border border-border rounded-sm p-3 hover:bg-secondary transition-colors flex items-center gap-3"
        >
          <FilePdf size={20} weight="fill" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Suspected duplicate of</p>
            <p className="font-semibold truncate text-sm">{old.vendor || old.filename}</p>
            <p className="text-xs text-muted-foreground truncate">{old.filename} · status: {old.status}</p>
          </div>
          <ArrowSquareOut size={18} className="text-muted-foreground shrink-0" />
        </button>

        {/* Actions */}
        <div className="space-y-2">
          <Button data-testid="resolve-file-duplicate" onClick={() => resolve("file_duplicate")} variant="outline" className="w-full justify-start rounded-sm font-semibold h-auto py-3">
            <Archive size={18} className="mr-2 shrink-0" />
            <span className="text-left"><span className="block">Stop import — file in Duplicates</span><span className="block text-xs font-normal text-muted-foreground">Keep in Archive › Duplicates; no ledger entries.</span></span>
          </Button>
          <Button data-testid="resolve-revision" onClick={() => resolve("revision")} variant="outline" className="w-full justify-start rounded-sm font-semibold h-auto py-3">
            <FileArrowUp size={18} className="mr-2 shrink-0" />
            <span className="text-left"><span className="block">Import as a revised bill</span><span className="block text-xs font-normal text-muted-foreground">Process this as an updated version.</span></span>
          </Button>
          <Button data-testid="resolve-not-duplicate" onClick={() => resolve("not_duplicate")} className="w-full justify-start rounded-sm font-semibold h-auto py-3">
            <CheckCircle size={18} weight="fill" className="mr-2 shrink-0" />
            <span className="text-left"><span className="block">Not a duplicate — proceed</span><span className="block text-xs font-normal opacity-80">Continue ingesting this statement normally.</span></span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
